import { useState, useMemo } from "react"
import { Target, TrendingUp, AlertTriangle, CheckCircle2, ChevronRight, Search } from "lucide-react"
import { Sidebar } from "../components/ui/sidebar"
import ProtectedRoute from "../components/ProtectedRoute"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"
import { getAllNodes } from "@/data/career-tree"

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getStatus(studentScore, threshold) {
  const ratio = studentScore / threshold
  if (ratio >= 0.9) return "strong"
  if (ratio >= 0.65) return "close"
  return "gap"
}

const STATUS_CONFIG = {
  strong: { label: "Strong", bar: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
  close:  { label: "Close",  bar: "bg-amber-400",   text: "text-amber-700",  bg: "bg-amber-50 border-amber-200"  },
  gap:    { label: "Gap",    bar: "bg-red-400",      text: "text-red-700",   bg: "bg-red-50 border-red-200"      },
}

const ACTION_MAP = {
  strong: (trait) => `Keep reinforcing ${trait} through regular practice and challenge activities.`,
  close:  (trait) => `You're nearly there on ${trait}. Focus one week of study sessions on this trait.`,
  gap:    (trait) => `${trait} needs the most work. Look for Journey modules and games that build this skill.`,
}

function TraitRow({ trait, studentScore, threshold }) {
  const status = getStatus(studentScore, threshold)
  const cfg = STATUS_CONFIG[status]
  const pct = Math.min(100, Math.round((studentScore / threshold) * 100))

  return (
    <div className={`rounded-xl border p-4 ${cfg.bg}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-bold text-slate-800">{trait.name}</span>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.text} bg-white/60`}>{cfg.label}</span>
      </div>
      <div className="flex items-center gap-3 mb-2">
        <div className="flex-1 h-2.5 bg-white/70 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${cfg.bar}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs font-bold text-slate-600 w-20 text-right shrink-0">
          {studentScore} / {threshold}
        </span>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">{ACTION_MAP[status](trait.name)}</p>
    </div>
  )
}

export default function SkillGapPage() {
  const [search, setSearch] = useState("")
  const [selectedId, setSelectedId] = useState("")

  const { traitProfile } = useMemo(() => {
    const currentUser = AuthStore.getState().currentUser
    const students = StorageService.read("students") ?? []
    const student = students.find(s => s.userId === currentUser?._id || s.email === currentUser?.email)
    return { traitProfile: student?.traitProfile ?? null }
  }, [])

  const fieldNodes = useMemo(() =>
    getAllNodes().filter(n => n.level === "field" && n.traits?.length > 0)
  , [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return fieldNodes
    return fieldNodes.filter(n =>
      n.name.toLowerCase().includes(q) || n.broadCategory?.toLowerCase().includes(q)
    )
  }, [fieldNodes, search])

  const selected = fieldNodes.find(n => n.nodeId === selectedId) ?? filtered[0] ?? null

  const analysis = useMemo(() => {
    if (!selected || !traitProfile) return null
    const traits = selected.traits.map(tr => ({
      ...tr,
      studentScore: traitProfile[tr.traitId] ?? 0,
      status: getStatus(traitProfile[tr.traitId] ?? 0, tr.minimumThreshold),
    }))
    const strong = traits.filter(t => t.status === "strong").length
    const close  = traits.filter(t => t.status === "close").length
    const gaps   = traits.filter(t => t.status === "gap").length
    const overall = Math.round((strong / traits.length) * 100)
    return { traits, strong, close, gaps, overall }
  }, [selected, traitProfile])

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />
        <main className="flex-1 lg:ml-64 p-4 md:p-8 overflow-y-auto">
          <div className="max-w-5xl mx-auto space-y-6">

            {/* Header */}
            <div>
              <h1 className="text-3xl font-black text-slate-900">Skill Gap Analysis</h1>
              <p className="text-slate-500 text-sm mt-1">See exactly where you stand for any career — and what to work on next.</p>
            </div>

            {!traitProfile && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                <p className="text-sm text-amber-800 font-medium">
                  Complete the Psychometric Assessment first to see your personalised skill gaps.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Career picker */}
              <div className="lg:col-span-4 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search career…"
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden max-h-[520px] overflow-y-auto">
                  {filtered.map(n => (
                    <button
                      key={n.nodeId}
                      onClick={() => setSelectedId(n.nodeId)}
                      className={`w-full text-left px-4 py-3 border-b border-slate-50 last:border-0 flex items-center gap-3 transition-colors ${
                        (selectedId === n.nodeId || (!selectedId && n.nodeId === filtered[0]?.nodeId))
                          ? "bg-indigo-50"
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{n.name}</p>
                        <p className="text-xs text-slate-400">{n.broadCategory}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                    </button>
                  ))}
                  {filtered.length === 0 && (
                    <p className="text-center py-8 text-sm text-slate-400">No careers found</p>
                  )}
                </div>
              </div>

              {/* Analysis panel */}
              <div className="lg:col-span-8 space-y-5">
                {selected && (
                  <>
                    <div className="bg-white rounded-2xl border border-slate-200 p-5">
                      <h2 className="text-xl font-black text-slate-900">{selected.name}</h2>
                      <p className="text-sm text-slate-500 mt-1">{selected.description}</p>

                      {analysis && (
                        <div className="mt-4 grid grid-cols-3 gap-3">
                          <div className="text-center bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                            <p className="text-2xl font-black text-emerald-600">{analysis.strong}</p>
                            <p className="text-xs text-emerald-600 font-semibold mt-0.5">Strong</p>
                          </div>
                          <div className="text-center bg-amber-50 rounded-xl p-3 border border-amber-100">
                            <p className="text-2xl font-black text-amber-600">{analysis.close}</p>
                            <p className="text-xs text-amber-600 font-semibold mt-0.5">Close</p>
                          </div>
                          <div className="text-center bg-red-50 rounded-xl p-3 border border-red-100">
                            <p className="text-2xl font-black text-red-600">{analysis.gaps}</p>
                            <p className="text-xs text-red-600 font-semibold mt-0.5">Gap</p>
                          </div>
                        </div>
                      )}

                      {analysis && (
                        <div className="mt-4">
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span className="font-semibold">Overall readiness</span>
                            <span className="font-bold text-indigo-600">{analysis.overall}%</span>
                          </div>
                          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-700"
                              style={{ width: `${analysis.overall}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {!traitProfile && (
                        <p className="mt-4 text-sm text-slate-400 italic">Complete the assessment to see your scores.</p>
                      )}
                    </div>

                    {/* Trait-by-trait breakdown */}
                    {analysis && (
                      <div className="space-y-3">
                        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide">Trait Breakdown</h3>
                        {analysis.traits
                          .sort((a, b) => {
                            const order = { gap: 0, close: 1, strong: 2 }
                            return order[a.status] - order[b.status]
                          })
                          .map(tr => (
                            <TraitRow
                              key={tr.traitId}
                              trait={tr}
                              studentScore={tr.studentScore}
                              threshold={tr.minimumThreshold}
                            />
                          ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

          </div>
        </main>
      </div>
    </ProtectedRoute>
  )
}
